/**
 * PlannerGrid Component
 *
 * Main grid showing weeks x strain families with demand/supply/gap data.
 * Displays shadow batches awaiting approval and real batches in production.
 * Integrates demand overrides so managers can adjust forecasts per cell.
 *
 * US-C2-006: Planner Page UI
 * US-C2-008: Demand Override Integration
 * Strain Families: groups by family (species) instead of individual strains
 */

import { useState, useMemo } from 'react'
import { usePlannerData } from '@/hooks/usePlannerData'
import { useDemandOverrides } from '@/hooks/useDemandOverrides'
import StrainCell from './StrainCell'
import AddBatchDialog from './AddBatchDialog'
import { Card } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, Calendar, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function PlannerGrid({ weekCount = 8, familyFilter = null, highlightWeek = null }) {
  const { weekData, loading, error, refetch } = usePlannerData({ weekCount })
  const { getOverrideForWeek, setOverride, removeOverride } = useDemandOverrides()
  const [showAddBatchDialog, setShowAddBatchDialog] = useState(false)
  const [selectedStrain, setSelectedStrain] = useState(null)
  const [selectedWeekStart, setSelectedWeekStart] = useState(null)

  // Auto-collapse weeks whose work week (Mon-Sat) has ended
  const initialCollapsed = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const collapsed = {}
    weekData.forEach(week => {
      const weekEnd = new Date(week.weekStart)
      weekEnd.setDate(weekEnd.getDate() + 6) // Saturday
      collapsed[week.weekStart] = today > weekEnd
    })
    return collapsed
  }, [weekData])

  const [collapsedWeeks, setCollapsedWeeks] = useState({})
  const isCollapsed = (weekStart) =>
    collapsedWeeks[weekStart] ?? initialCollapsed[weekStart] ?? false

  const toggleWeek = (weekStart) => {
    setCollapsedWeeks(prev => ({
      ...prev,
      [weekStart]: !isCollapsed(weekStart)
    }))
  }

  const handleAddBatch = (familyData, weekStart) => {
    // Use the family's default strain for batch creation
    setSelectedStrain(familyData.defaultStrainId)
    setSelectedWeekStart(weekStart)
    setShowAddBatchDialog(true)
  }

  const handleBatchAdded = () => {
    refetch()
  }

  const formatWeekHeader = (weekStart) => {
    const date = new Date(weekStart)
    const weekNum = getWeekNumber(date)
    const endDate = new Date(date)
    endDate.setDate(endDate.getDate() + 6)

    return {
      weekNum,
      dateRange: `${date.getDate()}-${endDate.getDate()} ${endDate.toLocaleDateString('en-GB', { month: 'short' })}`
    }
  }

  const getWeekNumber = (date) => {
    const startOfYear = new Date(date.getFullYear(), 0, 1)
    const diff = date - startOfYear
    const oneWeek = 1000 * 60 * 60 * 24 * 7
    return Math.ceil((diff / oneWeek) + 1)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(j => (
                <Skeleton key={j} className="h-48" />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (weekData.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-medium text-gray-900">No demand data found</p>
        <p className="text-sm text-gray-600 mt-1">
          No recurring orders or AI suggestions for the next {weekCount} weeks.
        </p>
      </Card>
    )
  }

  // Apply family filter if set
  const filteredWeekData = familyFilter
    ? weekData.map(week => ({
        ...week,
        families: week.families.filter(f => f.familyId === familyFilter)
      })).filter(week => week.families.length > 0)
    : weekData

  return (
    <>
      <div className="space-y-8">
        {filteredWeekData.map(week => {
          const { weekNum, dateRange } = formatWeekHeader(week.weekStart)
          const totalBlocks = week.families.reduce((sum, f) =>
            sum + [...f.shadowBatches, ...f.realBatches].reduce((bSum, b) => bSum + (b.total_blocks || 0), 0)
          , 0)
          const isHighlightedWeek = highlightWeek && week.weekStart === highlightWeek
          const collapsed = isCollapsed(week.weekStart)
          const weekPast = initialCollapsed[week.weekStart]

          return (
            <div
              key={week.weekStart}
              className={cn(
                'space-y-4',
                isHighlightedWeek && 'ring-2 ring-yellow-400 rounded-lg p-3 bg-yellow-50/30'
              )}
              ref={isHighlightedWeek ? (el) => el?.scrollIntoView({ behavior: 'smooth', block: 'start' }) : undefined}
            >
              {/* Week Header */}
              <div
                className={cn(
                  'flex items-center gap-3 pb-2 border-b cursor-pointer select-none',
                  weekPast && 'opacity-60'
                )}
                onClick={() => toggleWeek(week.weekStart)}
              >
                {collapsed
                  ? <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                  : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                }
                <h3 className="text-lg font-semibold text-gray-900">
                  W{weekNum}
                </h3>
                <span className="text-sm text-gray-600">
                  {dateRange}
                </span>
                {totalBlocks > 0 && (
                  <span className="text-sm text-muted-foreground font-medium">
                    {totalBlocks} blocks
                  </span>
                )}
                <div className="flex-1" />
                <span className="text-xs text-gray-500">
                  {week.families.length} famil{week.families.length !== 1 ? 'ies' : 'y'}
                </span>
              </div>

              {/* Family Cells Grid */}
              {!collapsed && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {week.families.map(familyData => (
                    <StrainCell
                      key={familyData.familyId}
                      familyData={familyData}
                      weekStart={week.weekStart}
                      override={getOverrideForWeek(familyData.familyId, week.weekStart)}
                      onSetOverride={setOverride}
                      onRemoveOverride={removeOverride}
                      onAddBatch={(data) => handleAddBatch(data, week.weekStart)}
                      onUpdate={refetch}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Add Batch Dialog */}
      <AddBatchDialog
        open={showAddBatchDialog}
        onOpenChange={setShowAddBatchDialog}
        onSuccess={handleBatchAdded}
        preselectedStrain={selectedStrain}
        preselectedWeekStart={selectedWeekStart}
      />
    </>
  )
}
